import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AdsBanner from '@/components/feature/AdsBanner';
import BlogCard from './components/BlogCard';
import { blogArticles, blogCategories } from '@/mocks/blog';
import { useScrollReveal } from '@/hooks/useScrollReveal';

function BlogPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState('all');

  const featured = blogArticles.filter((a) => a.featured)[0];
  const popular = blogArticles.filter((a) => a.popular);
  const recent = [...blogArticles].sort(
    (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
  ).slice(0, 3);
  const recommended = blogArticles.filter((a) => a.recommended);

  const filtered = useMemo(() => {
    return blogArticles.filter((a) => {
      const matchesQuery =
        searchQuery.trim() === '' ||
        a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.tags.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesCategory = category === 'all' || a.category === category;

      return matchesQuery && matchesCategory;
    });
  }, [searchQuery, category]);

  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal(0.05);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-10 md:pb-14">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block text-sm font-semibold text-primary-400 uppercase tracking-wider mb-3">
              WorldApeiron Media
            </span>
            <h1 className="font-heading font-bold text-3xl md:text-5xl lg:text-6xl text-foreground-50 mb-4">
              Idées, expertise et <span className="text-gradient-primary">actualités</span>
            </h1>
            <p className="text-base md:text-lg text-foreground-400 max-w-2xl mx-auto mb-6">
              Le média professionnel de WorldApeiron, animé par Web Entrepreneur Matthew DOTSE.
              Entrepreneuriat digital, technologies web, intelligence artificielle et innovation à l'échelle africaine.
            </p>

            {/* Author identity bar */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <Link to="/author/matthew-dotse" className="flex items-center gap-3 p-2 pr-4 rounded-full bg-background-100 border border-background-200/70 hover:border-primary-400/40 transition-colors">
                <img
                  src="https://static.readdy.ai/image/4348d35aef10b02e64c1a80f787cca17/83d58e97feb31105ec513f51514567d5.png"
                  alt="Matthew DOTSE"
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div className="text-left">
                  <span className="block text-sm font-medium text-foreground-50">Web Entrepreneur Matthew DOTSE</span>
                  <span className="block text-xs text-foreground-400">Fondateur & Rédacteur en chef</span>
                </div>
              </Link>
            </div>

            <form onSubmit={(e) => e.preventDefault()} className="max-w-xl mx-auto">
              <div className="flex items-center bg-background-100/80 backdrop-blur-md border border-background-300/60 rounded-lg overflow-hidden">
                <div className="w-12 h-12 flex items-center justify-center shrink-0">
                  <i className="ri-search-line text-lg text-foreground-500" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher un article, un sujet, un mot-clé..."
                  className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-3 outline-none"
                />
              </div>
            </form>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 pt-8">
        <AdsBanner pageKey="blog" />
      </section>

      {/* Featured article */}
      {featured && searchQuery === '' && category === 'all' && (
        <section className="w-full px-4 md:px-8 lg:px-12 pb-12">
          <div className="relative rounded-2xl overflow-hidden group cursor-pointer">
            <Link to={`/blog/${featured.slug}`} className="block">
              <div className="h-[360px] md:h-[440px] w-full overflow-hidden">
                <img
                  src={featured.coverImage}
                  alt={featured.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/50 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-primary-500 text-foreground-950">
                    À la une
                  </span>
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-background-50/90 backdrop-blur-sm text-primary-400">
                    {featured.category}
                  </span>
                </div>
                <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50 mb-3 max-w-3xl">
                  {featured.title}
                </h2>
                <p className="text-foreground-400 max-w-2xl mb-5 text-base">{featured.excerpt}</p>
                <div className="flex items-center gap-4">
                  <img
                    src={featured.authorImage}
                    alt={featured.author}
                    className="w-9 h-9 rounded-full object-cover"
                  />
                  <div>
                    <span className="block text-sm font-medium text-foreground-50">{featured.author}</span>
                    <span className="block text-xs text-foreground-400">{featured.publishedAt} · {featured.readTime} min</span>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Categories */}
      <section className="w-full px-4 md:px-8 lg:px-12 pb-8">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setCategory('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors whitespace-nowrap ${
              category === 'all'
                ? 'bg-primary-500 text-foreground-950 border-primary-500'
                : 'bg-background-100 text-foreground-500 border-background-300/60 hover:border-primary-400/40'
            }`}
          >
            Tous
          </button>
          {blogCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors whitespace-nowrap ${
                category === cat
                  ? 'bg-primary-500 text-foreground-950 border-primary-500'
                  : 'bg-background-100 text-foreground-500 border-background-300/60 hover:border-primary-400/40'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Articles grid */}
      <section ref={gridRef} className="w-full px-4 md:px-8 lg:px-12 pb-16">
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-article-line text-2xl text-foreground-400" />
            </div>
            <p className="text-foreground-500 font-medium mb-2">Aucun article trouvé</p>
            <p className="text-sm text-foreground-500">Essayez une autre recherche ou catégorie.</p>
          </div>
        ) : (
          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 transition-all duration-1000 ${gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            {filtered.map((article) => (
              <BlogCard key={article.id} article={article} />
            ))}
          </div>
        )}
      </section>

      {/* Popular & Recommended side columns */}
      {searchQuery === '' && category === 'all' && (
        <section className="w-full px-4 md:px-8 lg:px-12 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Popular */}
            <div>
              <h2 className="font-heading font-bold text-xl text-foreground-50 mb-6 flex items-center gap-2">
                <i className="ri-fire-line text-primary-400" />
                Articles populaires
              </h2>
              <div className="space-y-4">
                {popular.map((article) => (
                  <Link
                    key={article.id}
                    to={`/blog/${article.slug}`}
                    className="flex items-center gap-4 p-3 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-400/40 transition-all"
                  >
                    <img src={article.coverImage} alt="" className="w-20 h-20 rounded-lg object-cover shrink-0" />
                    <div className="min-w-0">
                      <h3 className="font-semibold text-sm text-foreground-50 leading-snug line-clamp-2">{article.title}</h3>
                      <span className="text-xs text-foreground-400">{article.category} · {article.views.toLocaleString('fr-FR')} vues</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Recommended & Recent */}
            <div>
              <h2 className="font-heading font-bold text-xl text-foreground-50 mb-6 flex items-center gap-2">
                <i className="ri-sparkling-line text-primary-400" />
                Recommandés pour vous
              </h2>
              <div className="space-y-4">
                {recommended.map((article) => (
                  <Link
                    key={article.id}
                    to={`/blog/${article.slug}`}
                    className="flex items-center gap-4 p-3 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-400/40 transition-all"
                  >
                    <img src={article.coverImage} alt="" className="w-20 h-20 rounded-lg object-cover shrink-0" />
                    <div className="min-w-0">
                      <h3 className="font-semibold text-sm text-foreground-50 leading-snug line-clamp-2">{article.title}</h3>
                      <span className="text-xs text-foreground-400">{article.category} · {article.readTime} min</span>
                    </div>
                  </Link>
                ))}
              </div>

              <h2 className="font-heading font-bold text-xl text-foreground-50 mb-6 mt-10 flex items-center gap-2">
                <i className="ri-time-line text-primary-400" />
                Articles récents
              </h2>
              <div className="space-y-4">
                {recent.map((article) => (
                  <Link
                    key={article.id}
                    to={`/blog/${article.slug}`}
                    className="flex items-center gap-4 p-3 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-400/40 transition-all"
                  >
                    <img src={article.coverImage} alt="" className="w-20 h-20 rounded-lg object-cover shrink-0" />
                    <div className="min-w-0">
                      <h3 className="font-semibold text-sm text-foreground-50 leading-snug line-clamp-2">{article.title}</h3>
                      <span className="text-xs text-foreground-400">{article.publishedAt} · {article.readTime} min</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}

export default BlogPage;